/**
 * WS4 Playback content — typed bundle of every artefact rendered by the
 * 4-tab `PlaybackModal`. Each tab is backed by its own typed data array
 * so the modal stays pure-presentational and the strings remain easy to
 * audit against the legacy source.
 *
 * Ported from:
 *   - `_legacy/ws4-assetlocker.js` lines 617-777 — the four
 *     `#pb-pane-*` panes of `#playbackModal`:
 *       * Overview  (lines 618-697): session metadata, the stat grid
 *         (Session Duration / Participants On Camera / Chapters / Key
 *         Moments Flagged), and the six `pb-chapter-row` entries.
 *       * Key Moments (699-712): eight `pb-hl-card` entries with an
 *         emoji icon, accent tint, and tag pill per moment.
 *       * Timeline (714-764): three stacked tracks (Session Flow /
 *         Speaking Time / Key Events) of positioned `pb-tl-segment`
 *         blocks, plus the seven-row Speaking Time Breakdown table.
 *       * Clips (766-777): six `pb-clip-card` thumbnails.
 *
 * The map is keyed by workshop id so future workshops can register
 * their own playback bundles. Today only WS4 is populated; consumers
 * should treat a missing key as "no playback registered".
 */

/** Accent slot for a chapter row — drives the chapter thumbnail tint. */
export type ChapterAccent =
  | "true-blue"
  | "gold"
  | "sky"
  | "melon"
  | "red"
  | "green";

export type PlaybackChapter = {
  id: string;
  title: string;
  /** mm:ss or h:mm marker shown in the chapter list (e.g. "00:00", "01:58"). */
  timestamp: string;
  /** Human duration label, e.g. "18 min", "55 min", "15 min". */
  duration: string;
  /** Short description shown under the title in the chapter row. */
  description: string;
  accent: ChapterAccent;
  /** Optional badge — used by the legacy "Break" row to flag pause time. */
  isBreak?: boolean;
  /** Optional alert badge — used by the legacy "Activity 2.3" row (ran 10 min over). */
  warning?: string;
  /** Decorative jump target in minutes — passed through to the scroll handler. */
  jumpMinutes: number;
};

/* ----------------------------- KEY MOMENTS -------------------------- */

/**
 * Accent slot for a key-moment card. Mirrors the per-card tint legacy
 * inlined as `rgba(var(--accent-*-rgb), 0.08-0.10)` plus a matching
 * coloured tag pill.
 */
export type KeyMomentAccent =
  | "sky"
  | "green"
  | "red"
  | "true-blue"
  | "gold"
  | "melon";

export type KeyMoment = {
  id: string;
  /** mm:ss marker shown left of the card. */
  timestamp: string;
  /** Emoji rendered in the coloured icon square. */
  icon: string;
  title: string;
  description: string;
  /** Tag pill text — "AI OBSERVER", "ACTION ITEM", "RISK FLAG", … */
  tagLabel: string;
  accent: KeyMomentAccent;
  /** Decorative jump target in minutes (Overview tab). */
  jumpMinutes: number;
};

/* ----------------------------- TIMELINE ----------------------------- */

/** Which of the three timeline tracks a segment belongs to. */
export type TimelineTrack = "flow" | "speaking" | "events";

/**
 * Accent slot for a timeline segment. The "muted" slot is used by the
 * Speaking Time track's filler blocks (transparent labels — see the
 * "color:transparent" segments in the legacy source).
 */
export type TimelineAccent =
  | "true-blue"
  | "gold"
  | "sky"
  | "gold-break"
  | "red-pink"
  | "green"
  | "melon"
  | "sky-fill"
  | "true-blue-fill";

export type TimelineSegment = {
  id: string;
  track: TimelineTrack;
  /** Label rendered inside the segment — may be empty for filler blocks. */
  label: string;
  /** CSS `left` percentage (0-100). */
  left: number;
  /** CSS `width` percentage (0-100). */
  width: number;
  accent: TimelineAccent;
  /** Optional italic-style override for the "Break" segment. */
  italic?: boolean;
};

/* ----------------------------- SPEAKING TIME ----------------------- */

/** Accent slot for a Speaking Time Breakdown row. */
export type SpeakerAccent =
  | "true-blue"
  | "sky"
  | "green"
  | "gold"
  | "melon"
  | "pink"
  | "muted";

export type SpeakerRow = {
  speaker: string;
  role: string;
  estTime: string;
  /** e.g. "27.8%". */
  percent: string;
  contribution: string;
  accent: SpeakerAccent;
};

/* ----------------------------- CLIPS ------------------------------- */

/** Accent slot for the clip card tag colour. */
export type ClipAccent = "green" | "true-blue" | "red" | "gold" | "melon";

export type Clip = {
  id: string;
  /** Title rendered under the thumb. */
  title: string;
  /** Timestamp on the meta line, e.g. "01:04". */
  timestamp: string;
  /** Speaker(s) on the meta line, e.g. "John C." or "XAES-AI + Marcus W.". */
  speaker: string;
  /** Coloured tag label on the meta line. */
  tagLabel: string;
  tagAccent: ClipAccent;
  /** Duration badge bottom-right of the thumb, e.g. "2:14". */
  duration: string;
  /** Decorative jump target in minutes (Overview tab). */
  jumpMinutes: number;
  /**
   * Decorative radial-gradient origin for the thumb background. Mirrors
   * the inline `background:radial-gradient(ellipse at X% Y%, …)` the
   * legacy painted into each card.
   */
  thumbGradientX: number;
  thumbGradientY: number;
};

/* ----------------------------- STAT CARDS -------------------------- */

export type PlaybackStat = {
  label: string;
  value: string;
  sub: string;
  /** Colour for the big value text. */
  accent: "sky" | "green" | "true-blue" | "gold";
};

/* ----------------------------- BUNDLE ------------------------------ */

export type WorkshopPlayback = {
  /** Session title block, e.g. "Human-Agent Ways of Working". */
  sessionTitle: string;
  /** Sub-title block, e.g. "XAES Phase Zero · Workshop 4 · 14 Mar 2026". */
  sessionSubtitle: string;
  /** Display duration badge, e.g. "3:28:14". */
  durationBadge: string;
  /** Compact time, e.g. "0:00 / 3:28". */
  scrubberLabel: string;
  /** Footer summary, e.g. "3h 28m · 6 CHAPTERS · 11 KEY MOMENTS · 6 CLIPS". */
  footerSummary: string;
  /** Title shown above the timeline ruler, e.g. "Session Timeline — 3h 28m · 14 Mar 2026". */
  timelineTitle: string;
  /** The eight ruler ticks ("0:00", "0:30", … "3:28"). */
  timelineTicks: ReadonlyArray<string>;
  /** Chapter markers along the scrubber, expressed as `left` percentages. */
  scrubberChapterMarkers: ReadonlyArray<{ left: number; title: string }>;
  stats: ReadonlyArray<PlaybackStat>;
  chapters: ReadonlyArray<PlaybackChapter>;
  keyMoments: ReadonlyArray<KeyMoment>;
  timelineSegments: ReadonlyArray<TimelineSegment>;
  speakingTime: ReadonlyArray<SpeakerRow>;
  clips: ReadonlyArray<Clip>;
};

/* ----------------------------- WS4 DATA ---------------------------- */

const WS4_PLAYBACK: WorkshopPlayback = {
  sessionTitle: "Human-Agent Ways of Working",
  sessionSubtitle: "XAES Phase Zero · Workshop 4 · 14 Mar 2026",
  durationBadge: "3:28:14",
  scrubberLabel: "0:00 / 3:28",
  footerSummary: "3h 28m · 6 CHAPTERS · 11 KEY MOMENTS · 6 CLIPS",
  timelineTitle: "Session Timeline — 3h 28m · 14 Mar 2026",
  timelineTicks: ["0:00", "0:30", "1:00", "1:30", "2:00", "2:30", "3:00", "3:28"],
  scrubberChapterMarkers: [
    { left: 8.6, title: "Opening" },
    { left: 17.3, title: "Activity 2.1" },
    { left: 51.9, title: "Activity 2.2" },
    { left: 56.2, title: "Break" },
    { left: 62.5, title: "Activity 2.3" },
  ],

  stats: [
    { label: "Session Duration", value: "3:28", sub: "Hours · Minutes", accent: "sky" },
    {
      label: "Participants On Camera",
      value: "18",
      sub: "of 24 total attendees",
      accent: "green",
    },
    { label: "Chapters", value: "6", sub: "Incl. break + opening", accent: "true-blue" },
    {
      label: "Key Moments Flagged",
      value: "11",
      sub: "Auto-tagged by XAES-AI",
      accent: "gold",
    },
  ],

  chapters: [
    {
      id: "opening",
      title: "Opening & Scene-Setting",
      timestamp: "00:00",
      duration: "18 min",
      description:
        "Jordan K. introduces the session. John C. is introduced as executive observer. XAES-AI begins transcription.",
      accent: "true-blue",
      jumpMinutes: 0,
    },
    {
      id: "activity-2-1",
      title: "Activity 2.1 — Role & Responsibility Mapping",
      timestamp: "00:18",
      duration: "52 min",
      description:
        "Groups map tasks onto RACI grid. John C. introduces \"execution vs accountability ownership\" distinction.",
      accent: "gold",
      jumpMinutes: 18,
    },
    {
      id: "activity-2-2",
      title: "Activity 2.2 — Operating Mode Calibration",
      timestamp: "01:10",
      duration: "48 min",
      description:
        "Teams assign In/Above/On/Out-of-Loop modes. XAES-AI clarifies the 2am coverage problem. \"Mode graduation\" concept coined.",
      accent: "sky",
      jumpMinutes: 70,
    },
    {
      id: "break",
      title: "Break",
      timestamp: "01:58",
      duration: "15 min",
      description:
        "15-minute break. Informal whiteboard discussion continues between Priya S. and Marcus W.",
      accent: "gold",
      isBreak: true,
      jumpMinutes: 118,
    },
    {
      id: "activity-2-3",
      title: "Activity 2.3 — Stress Test Scenarios",
      timestamp: "02:13",
      duration: "55 min",
      description:
        "3 of 4 scenarios tested. Threshold drift risk flagged by XAES-AI. Red-team injection concept proposed. Session runs 10 min over.",
      accent: "red",
      warning: "10 min over",
      jumpMinutes: 133,
    },
    {
      id: "close",
      title: "Close & Next Steps",
      timestamp: "03:08",
      duration: "35 min",
      description:
        "Jordan K. summarises outputs. John C. gives closing remarks. XAES-AI confirms bundle distribution by 09:00 the following day.",
      accent: "green",
      jumpMinutes: 208,
    },
  ],

  keyMoments: [
    {
      id: "km-xaes-on",
      timestamp: "00:04",
      icon: "🤖",
      title: "XAES-AI Transcription Goes Live",
      description:
        "XAES-AI confirms auto-transcription is active and introduces the [ACTION] tagging system to participants.",
      tagLabel: "AI OBSERVER",
      accent: "sky",
      jumpMinutes: 4,
    },
    {
      id: "km-exec-acc",
      timestamp: "01:04",
      icon: "⚡",
      title: "Execution vs Accountability Ownership",
      description:
        "John C. introduces the concept of separating execution ownership from accountability ownership — flagged for charter language by Jordan K.",
      tagLabel: "ACTION ITEM",
      accent: "green",
      jumpMinutes: 64,
    },
    {
      id: "km-automation-bias",
      timestamp: "00:52",
      icon: "🚩",
      title: "Automation Bias Risk Raised",
      description:
        "Marcus W. questions whether human reviewers in In-the-Loop mode are meaningfully reviewing or just rubber-stamping. Flagged for Activity 2.3.",
      tagLabel: "RISK FLAG",
      accent: "red",
      jumpMinutes: 42,
    },
    {
      id: "km-mode-graduation",
      timestamp: "01:52",
      icon: "🏆",
      title: "\"Mode Graduation\" Coined",
      description:
        "John C. proposes tasks should \"graduate\" to Out-of-Loop mode only after 6 months of clean On-the-Loop operation. Jordan K. adds it to charter language immediately.",
      tagLabel: "CHARTER LANGUAGE",
      accent: "true-blue",
      jumpMinutes: 112,
    },
    {
      id: "km-threshold-drift",
      timestamp: "02:19",
      icon: "⚠️",
      title: "Threshold Drift — Critical Risk Pattern",
      description:
        "XAES-AI flags threshold drift as one of the top 3 root causes of agentic incidents in deployment post-mortems. Priya S. moves to make dual sign-off mandatory.",
      tagLabel: "CRITICAL RISK",
      accent: "red",
      jumpMinutes: 139,
    },
    {
      id: "km-news-signal",
      timestamp: "02:31",
      icon: "📡",
      title: "News Signal Integration Proposed",
      description:
        "John C. proposes agents should detect breaking news signals thematically related to queued content and auto-flag for human review before publishing.",
      tagLabel: "DESIGN PROPOSAL",
      accent: "gold",
      jumpMinutes: 151,
    },
    {
      id: "km-red-team",
      timestamp: "02:49",
      icon: "🔬",
      title: "Red-Team Injection Concept — XAES-AI",
      description:
        "XAES-AI proposes randomly injecting flagged errors into ~2% of outputs to test reviewer alertness — a pattern from healthcare and financial services deployments.",
      tagLabel: "AI INPUT",
      accent: "melon",
      jumpMinutes: 169,
    },
    {
      id: "km-close",
      timestamp: "03:08",
      icon: "✅",
      title: "Session Close — John C. Closing Remarks",
      description:
        "John C. notes the quality of governance thinking, calling the session \"not something you get from a slide deck.\" Jordan K. confirms WS5 date.",
      tagLabel: "SESSION CLOSE",
      accent: "green",
      jumpMinutes: 208,
    },
  ],

  timelineSegments: [
    // ─── Session Flow ──────────────────────────────────────────────
    { id: "tl-flow-opening", track: "flow", label: "Opening", left: 0, width: 8.6, accent: "true-blue" },
    {
      id: "tl-flow-21",
      track: "flow",
      label: "Activity 2.1 — Role Mapping",
      left: 8.6,
      width: 25,
      accent: "gold",
    },
    {
      id: "tl-flow-22",
      track: "flow",
      label: "Activity 2.2 — Mode Calibration",
      left: 33.6,
      width: 22.6,
      accent: "sky",
    },
    {
      id: "tl-flow-break",
      track: "flow",
      label: "Break",
      left: 56.2,
      width: 7.2,
      accent: "gold-break",
      italic: true,
    },
    {
      id: "tl-flow-23",
      track: "flow",
      label: "Activity 2.3 — Stress Test",
      left: 63.4,
      width: 26.5,
      accent: "red-pink",
    },
    { id: "tl-flow-close", track: "flow", label: "Close", left: 89.9, width: 10.1, accent: "green" },

    // ─── Speaking Time ────────────────────────────────────────────
    // Jordan K. — labelled + 5 filler blocks (legacy used colour:transparent
    // to keep the silhouette without repeating the name).
    {
      id: "tl-speak-jordan-1",
      track: "speaking",
      label: "Jordan K.",
      left: 0,
      width: 8,
      accent: "true-blue-fill",
    },
    { id: "tl-speak-jordan-2", track: "speaking", label: "", left: 10, width: 4, accent: "true-blue-fill" },
    { id: "tl-speak-jordan-3", track: "speaking", label: "", left: 17, width: 6, accent: "true-blue-fill" },
    { id: "tl-speak-jordan-4", track: "speaking", label: "", left: 34, width: 3, accent: "true-blue-fill" },
    { id: "tl-speak-jordan-5", track: "speaking", label: "", left: 64, width: 5, accent: "true-blue-fill" },
    { id: "tl-speak-jordan-6", track: "speaking", label: "", left: 87, width: 5, accent: "true-blue-fill" },
    // XAES-AI bursts — sky tint.
    { id: "tl-speak-xaes-1", track: "speaking", label: "", left: 3, width: 1.5, accent: "sky-fill" },
    { id: "tl-speak-xaes-2", track: "speaking", label: "", left: 25, width: 1, accent: "sky-fill" },
    { id: "tl-speak-xaes-3", track: "speaking", label: "", left: 42, width: 1.5, accent: "sky-fill" },
    { id: "tl-speak-xaes-4", track: "speaking", label: "", left: 68, width: 2, accent: "sky-fill" },

    // ─── Key Events ───────────────────────────────────────────────
    { id: "tl-evt-xaes-on", track: "events", label: "XAES on", left: 3, width: 2, accent: "sky" },
    { id: "tl-evt-risk", track: "events", label: "Risk F.", left: 25, width: 3.5, accent: "red-pink" },
    { id: "tl-evt-mode", track: "events", label: "Mode↑", left: 53, width: 3, accent: "true-blue" },
    {
      id: "tl-evt-threshold",
      track: "events",
      label: "Threshold",
      left: 64,
      width: 4,
      accent: "red-pink",
    },
    {
      id: "tl-evt-redteam",
      track: "events",
      label: "Red-team",
      left: 72,
      width: 4,
      accent: "melon",
    },
    { id: "tl-evt-close", track: "events", label: "Close", left: 89, width: 5, accent: "green" },
  ],

  speakingTime: [
    {
      speaker: "Jordan K.",
      role: "Facilitator",
      estTime: "58 min",
      percent: "27.8%",
      contribution: "Facilitation, scenario delivery, charter language",
      accent: "true-blue",
    },
    {
      speaker: "XAES-AI",
      role: "AI Observer",
      estTime: "14 min",
      percent: "6.7%",
      contribution: "Risk flags, AI inputs, action logging",
      accent: "sky",
    },
    {
      speaker: "John C.",
      role: "Exec Observer",
      estTime: "22 min",
      percent: "10.6%",
      contribution: "Governance framing, mode graduation, closing remarks",
      accent: "green",
    },
    {
      speaker: "Priya S.",
      role: "Ops Lead",
      estTime: "19 min",
      percent: "9.1%",
      contribution: "Escalation protocol, threshold governance",
      accent: "gold",
    },
    {
      speaker: "Marcus W.",
      role: "Tech Lead",
      estTime: "16 min",
      percent: "7.7%",
      contribution: "Automation bias, precedent contradiction trigger",
      accent: "melon",
    },
    {
      speaker: "Sam R.",
      role: "Change & Comms",
      estTime: "12 min",
      percent: "5.8%",
      contribution: "Publish hold window, red-team pilot support",
      accent: "pink",
    },
    {
      speaker: "All other participants",
      role: "Mixed",
      estTime: "67 min",
      percent: "32.3%",
      contribution: "Group work readouts, discussion contributions",
      accent: "muted",
    },
  ],

  clips: [
    {
      id: "clip-exec-acc",
      title: "Execution vs Accountability Ownership",
      timestamp: "01:04",
      speaker: "John C.",
      tagLabel: "Charter Language",
      tagAccent: "green",
      duration: "2:14",
      jumpMinutes: 64,
      thumbGradientX: 40,
      thumbGradientY: 50,
    },
    {
      id: "clip-mode-grad",
      title: "\"Mode Graduation\" — John C.",
      timestamp: "01:52",
      speaker: "John C.",
      tagLabel: "Key Concept",
      tagAccent: "true-blue",
      duration: "1:48",
      jumpMinutes: 112,
      thumbGradientX: 60,
      thumbGradientY: 40,
    },
    {
      id: "clip-threshold",
      title: "Threshold Drift Risk — XAES-AI Flag",
      timestamp: "02:19",
      speaker: "XAES-AI",
      tagLabel: "Critical Risk",
      tagAccent: "red",
      duration: "3:02",
      jumpMinutes: 139,
      thumbGradientX: 50,
      thumbGradientY: 60,
    },
    {
      id: "clip-publish-hold",
      title: "Mandatory Publish Hold Window",
      timestamp: "02:30",
      speaker: "Sam R. + John C.",
      tagLabel: "Protocol Design",
      tagAccent: "gold",
      duration: "2:26",
      jumpMinutes: 151,
      thumbGradientX: 35,
      thumbGradientY: 55,
    },
    {
      id: "clip-red-team",
      title: "Red-Team Injection — XAES-AI Proposes",
      timestamp: "02:49",
      speaker: "XAES-AI + Marcus W.",
      tagLabel: "AI Input",
      tagAccent: "melon",
      duration: "4:10",
      jumpMinutes: 169,
      thumbGradientX: 55,
      thumbGradientY: 45,
    },
    {
      id: "clip-close",
      title: "John C. Closing Remarks",
      timestamp: "03:40",
      speaker: "John C.",
      tagLabel: "Session Close",
      tagAccent: "green",
      duration: "2:55",
      jumpMinutes: 220,
      thumbGradientX: 45,
      thumbGradientY: 50,
    },
  ],
};

/** Workshop id → playback bundle. */
export const playbackByWorkshop: Record<string, WorkshopPlayback> = {
  WS4: WS4_PLAYBACK,
  ws4: WS4_PLAYBACK,
};

export function getPlayback(workshopId: string): WorkshopPlayback | null {
  return playbackByWorkshop[workshopId] ?? null;
}
